/**
 * "Pending" tool state parked in localStorage before login and restored after.
 *
 * Every AI tool hook re-implemented the same shape: stamp a timestamp, JSON
 * round-trip, and expire after an hour. Only the storage key and the state
 * shape vary — those stay at the call site; the mechanics live here.
 *
 * Pure over the `localStorage` global, so it is unit-tested in node with a
 * stubbed store (see test/pending-state.test.mjs).
 */

const TIMESTAMP_KEY = "timestamp";

/** Persist state under `key`, stamping it with the current time. */
export function savePendingState<T extends object>(key: string, data: T): void {
	try {
		localStorage.setItem(
			key,
			JSON.stringify({ ...data, [TIMESTAMP_KEY]: Date.now() }),
		);
	} catch {
		// storage unavailable / quota — nothing to restore later, ignore
	}
}

/**
 * Load state saved under `key`. Returns null (and clears the key) when the
 * entry is missing, unparseable, or older than `maxAgeMs`.
 */
export function loadPendingState<T>(
	key: string,
	{ maxAgeMs }: { maxAgeMs: number },
): T | null {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) {
			return null;
		}

		const parsed = JSON.parse(raw) as Record<string, unknown>;
		const timestamp = parsed?.[TIMESTAMP_KEY] as number | undefined;

		if (!timestamp || Date.now() - timestamp > maxAgeMs) {
			localStorage.removeItem(key);
			return null;
		}

		const { [TIMESTAMP_KEY]: _ts, ...data } = parsed;
		return data as T;
	} catch {
		return null;
	}
}

/** Remove any pending state stored under `key`. */
export function clearPendingState(key: string): void {
	try {
		localStorage.removeItem(key);
	} catch {
		// ignore
	}
}

/* -------------------------------------------------------------------------
 * IndexedDB twins — for state that carries images.
 *
 * localStorage only holds strings, so an image has to go in as base64 (+33%
 * bigger) and the whole origin shares a 5MB quota. IndexedDB stores Blobs
 * natively via structured clone and its quota is a slice of disk, so state
 * with images belongs here. Same key/expiry contract as the sync pair above.
 * ---------------------------------------------------------------------- */

/**
 * 图片生成器登录前暂存的现场。/ai-edit-image 渲染的也是 AiImageGeneratorClient，
 * 两条路走同一个 key。
 */
export const PENDING_GENERATOR_STATE_KEY = "pendingGeneratorState";

/** photo-to-anime / age-filter 等转换页共用的现场 */
export const PENDING_TRANSFORMATION_STATE_KEY = "pendingTransformationState";

const DB_NAME = "pending-state";
const DB_VERSION = 1;
const STORE_NAME = "state";

function openDatabase(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);
		request.onupgradeneeded = () => {
			if (!request.result.objectStoreNames.contains(STORE_NAME)) {
				request.result.createObjectStore(STORE_NAME);
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

function runRequest<T>(request: IDBRequest<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

/**
 * Persist state that may contain Blobs. Failures (private mode, quota) are
 * swallowed: not being able to restore later must never block the login it
 * is about to trigger.
 */
export async function saveBlobPendingState<T extends object>(
	key: string,
	data: T,
): Promise<void> {
	try {
		const db = await openDatabase();
		const store = db
			.transaction(STORE_NAME, "readwrite")
			.objectStore(STORE_NAME);
		await runRequest(
			store.put({ ...data, [TIMESTAMP_KEY]: Date.now() }, key),
		);
		db.close();
	} catch (error) {
		console.error("Failed to save pending state:", error);
	}
}

/**
 * Read the state saved under `key` without removing it. Returns null when the
 * entry is missing or older than `maxAgeMs`. Callers that must survive a
 * remount (email login triggers `router.replace`) read with this and clear
 * separately; everyone else wants `takeBlobPendingState`.
 */
export async function readBlobPendingState<T>(
	key: string,
	{ maxAgeMs }: { maxAgeMs: number },
): Promise<T | null> {
	try {
		const db = await openDatabase();
		const store = db
			.transaction(STORE_NAME, "readonly")
			.objectStore(STORE_NAME);
		const stored = (await runRequest(store.get(key))) as
			| Record<string, unknown>
			| undefined;
		db.close();

		const timestamp = stored?.[TIMESTAMP_KEY] as number | undefined;
		if (!stored || !timestamp || Date.now() - timestamp > maxAgeMs) {
			return null;
		}

		const { [TIMESTAMP_KEY]: _ts, ...data } = stored;
		return data as T;
	} catch (error) {
		console.error("Failed to read pending state:", error);
		return null;
	}
}

/** Remove the state saved under `key`. */
export async function clearBlobPendingState(key: string): Promise<void> {
	try {
		const db = await openDatabase();
		const store = db
			.transaction(STORE_NAME, "readwrite")
			.objectStore(STORE_NAME);
		await runRequest(store.delete(key));
		db.close();
	} catch (error) {
		console.error("Failed to clear pending state:", error);
	}
}

/**
 * Read and delete in one step. Reading consumes it — a restored form should
 * not come back again on the next login.
 */
export async function takeBlobPendingState<T>(
	key: string,
	options: { maxAgeMs: number },
): Promise<T | null> {
	const state = await readBlobPendingState<T>(key, options);
	await clearBlobPendingState(key);
	return state;
}
